import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { X, MessageSquare, Users, ArrowLeft } from "lucide-react";
import { Heading, Text } from "@/components/Typography";
import { Button } from "@/components/Button";
import { useAuth } from "@/hooks/useAuth";
import { MessageAttachments } from "@/components/MessageAttachments";
import { MessageBody } from "@/components/MessageBody";
import { MessageComposer } from "@/components/MessageComposer";
import { useMessageAttachments } from "@/hooks/useMessageAttachments";
import { useActiveWorkspace } from "@/hooks/useActiveWorkspace";
import { getPrimaryRole } from "@/utils/roleUtils";
import { tracking } from "@/utils/tracking";
import { usePabSession } from "@/hooks/usePabSession";
import { useAdvisorPABs } from "@/hooks/useDashboardStats";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, PabMessage, programsApi } from "@/services/api";
import { formatDisplayDate } from "@/utils/formatDisplayDate";
import { useExpertPrograms } from "@/hooks/useExpertPrograms";
import { SelectRoot } from "@/components/Select";

type ConversationTab = "pab" | "program";

/** A person the current user can send a direct message to, in either PAB or Program tab. */
interface RecipientOption {
  userId: string;
  name: string;
  avatarUrl?: string | null;
}

const EVERYONE_VALUE = "everyone";

const formatMessageTime = (dateStr: string): string => {
  return formatDisplayDate(dateStr);
};

const isAdvisorAccepted = (approvalStatus?: string) => {
  const normalized = (approvalStatus || "").toLowerCase();
  return normalized === "accepted" || normalized === "approved";
};

const ConversationState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">{children}</div>
);

const JoinedConversationNotices: React.FC<{
  advisorNames: string[];
  className?: string;
}> = ({ advisorNames, className = "" }) => {
  if (advisorNames.length === 0) return null;

  return (
    <div className={`space-y-2 ${className}`.trim()}>
      {advisorNames.map((advisorName) => (
        <div key={advisorName} className="flex justify-center">
          <Text as="p" className="font-inter text-12 text-theme-text-muted text-center">
            {advisorName} joined the conversation
          </Text>
        </div>
      ))}
    </div>
  );
};

/** A send now carries text and/or already-uploaded attachment ids. */
interface SendPayload {
  body: string;
  attachmentIds: number[];
}

const MessageBubble: React.FC<{
  message: PabMessage;
  currentUserId?: string | number;
  currentUserRole?: string;
  /** Programme the conversation belongs to — needed to address the resource
   *  endpoints when an attachment is downloaded. */
  programId?: number | null;
}> = ({ message, currentUserId, currentUserRole, programId }) => {
  const isCurrentUser = message.senderId === currentUserId;
  const displayName = isCurrentUser ? "You" : message.senderName;
  const roleLabel = isCurrentUser
    ? currentUserRole === "advisor"
      ? "Expert"
      : "Achiever"
    : currentUserRole === "advisor"
      ? "Achiever"
      : "Expert";

  return (
    <div className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[75%] rounded-2xl px-4 pt-3 pb-3 ${
          isCurrentUser ? "bg-theme-msg-bubble-self-bg" : "bg-theme-msg-bubble-other-bg"
        }`}
      >
        <div className="mb-1 flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-col">
            <Text
              as="span"
              className={`font-inter text-14 font-semibold ${
                isCurrentUser
                  ? "text-theme-msg-bubble-self-name"
                  : "text-theme-msg-bubble-other-name"
              }`}
            >
              {displayName}
            </Text>
            <Text as="span" className="font-inter text-12 text-theme-text-muted">
              {roleLabel}
            </Text>
          </div>
          <Text as="span" className="font-inter text-12 text-theme-text-muted flex-shrink-0">
            {formatMessageTime(message.createdAt)}
          </Text>
        </div>
        {/* An attachment-only message has an empty body — render nothing
            rather than an empty paragraph that adds stray spacing.
            MessageBody, not raw text: the composer sends its formatting as
            markdown, so printing the string would show the markers. */}
        {message.body && (
          <MessageBody
            text={message.body}
            className="font-inter text-14 text-theme-text leading-6"
          />
        )}
        <MessageAttachments attachments={message.attachments ?? []} programId={programId} />
      </div>
    </div>
  );
};

const MessageSkeleton = () => (
  <div className="space-y-4">
    {[1, 2].map((i) => (
      <div key={i} className={`flex ${i % 2 === 0 ? "justify-start" : "justify-end"}`}>
        <div className="max-w-[75%] rounded-2xl bg-theme-bg-surface-raised h-16 animate-pulse" />
      </div>
    ))}
  </div>
);

export interface MessagesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  sessionId?: number;
  // v3: advisor identity is the user_id (SHA256 string), not an int PK.
  initialSelectedAdvisorId?: string | null;
  initialProgramExternalId?: string;
  chatTitle?: string;
  chatSubtitle?: string;
  /** Advisor: open the PAB's group thread ("Everyone") instead of the default
   *  1:1 with the achiever — set by the inbox's group-chat rows. */
  openGroupThread?: boolean;
}

export const MessagesDrawer: React.FC<MessagesDrawerProps> = ({
  isOpen,
  onClose,
  onBack,
  sessionId: propSessionId,
  initialSelectedAdvisorId,
  initialProgramExternalId,
  chatTitle,
  chatSubtitle,
  openGroupThread = false,
}) => {
  const { user } = useAuth();
  const isAdvisor = useActiveWorkspace() === "advisor";
  const { session, isLoading: sessionLoading } = usePabSession();
  const { data: advisorPabs } = useAdvisorPABs({
    enabled: isAdvisor && isOpen,
  });
  const {
    expertPrograms,
    advisorPrograms,
    peerPrograms,
    facilitatedPeerPrograms,
    isLoading: programsLoading,
  } = useExpertPrograms();
  const queryClient = useQueryClient();

  // Peer groups sit alongside Expert Programs in the Program tab. Messaging is
  // type-agnostic end to end -- the conversation is keyed on programs.id and
  // both membership gates accept any role -- so a peer group needs no separate
  // tab, only inclusion in the list. `useExpertPrograms` keeps the two apart on
  // purpose (see its comment), which is why this joins them explicitly rather
  // than the hook doing it.
  const userPrograms = useMemo(
    () =>
      isAdvisor ? [...advisorPrograms, ...facilitatedPeerPrograms] : [...expertPrograms, ...peerPrograms],
    [isAdvisor, advisorPrograms, facilitatedPeerPrograms, expertPrograms, peerPrograms],
  );

  // ── Availability flags ────────────────────────────────────────
  // pabDataReady: true once the PAB data fetch is settled (not still loading)
  const pabDataReady = isAdvisor ? advisorPabs !== undefined : !sessionLoading;
  const hasPab = pabDataReady && (isAdvisor ? (advisorPabs?.length ?? 0) > 0 : !!session?.id);
  const pabEmpty = pabDataReady && !hasPab;
  const hasPrograms = !programsLoading && userPrograms.length > 0;
  const programsEmpty = !programsLoading && !hasPrograms;
  // Show the type picker only when the user has access to both.
  // When only one type is available the auto-select effect below picks it automatically.
  const showConversationPicker = hasPab && hasPrograms;

  // ── Shared UI state ───────────────────────────────────────────
  const [selectedTab, setSelectedTab] = useState<ConversationTab | null>(null);
  const [messageInput, setMessageInput] = useState("");
  const [sending, setSending] = useState(false);


  // ── PAB state ────────────────────────────────────────────────
  const [selectedAchieverSessionId, setSelectedAchieverSessionId] = useState<number | null>(null);
  // null = "Everyone" (group thread); otherwise the userId of a direct-message recipient
  const [selectedPabRecipientUserId, setSelectedPabRecipientUserId] = useState<string | null>(null);
  const previousPropSessionIdRef = useRef<number | undefined>(undefined);
  const wasDrawerOpenRef = useRef(false);

  // ── Program state ─────────────────────────────────────────────
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [selectedProgramExternalId, setSelectedProgramExternalId] = useState<string | null>(null);
  // null = "Everyone" (group thread); otherwise the userId of a direct-message recipient
  const [selectedProgramRecipientUserId, setSelectedProgramRecipientUserId] = useState<
    string | null
  >(null);

  // Reset all state on close; auto-select tab if opened with PAB or program context
  useEffect(() => {
    if (!isOpen) {
      setSelectedTab(null);
      setSelectedProgramId(null);
      setSelectedProgramExternalId(null);
      setSelectedProgramRecipientUserId(null);
      setSelectedPabRecipientUserId(null);
      wasDrawerOpenRef.current = false;
      return;
    }
    if (propSessionId || initialSelectedAdvisorId) {
      setSelectedTab("pab");
    } else if (initialProgramExternalId) {
      setSelectedTab("program");
    }
  }, [isOpen, propSessionId, initialSelectedAdvisorId, initialProgramExternalId]);

  // Auto-select tab when only one conversation type is available.
  // Waits until both data sources have settled so we don't flip prematurely.
  useEffect(() => {
    if (!isOpen || selectedTab !== null) return;
    if (propSessionId || initialSelectedAdvisorId || initialProgramExternalId) return;
    if (!pabDataReady || programsLoading) return;

    if (pabEmpty && hasPrograms) setSelectedTab("program");
    else if (programsEmpty && hasPab) setSelectedTab("pab");
  }, [
    isOpen,
    selectedTab,
    pabDataReady,
    programsLoading,
    pabEmpty,
    hasPrograms,
    programsEmpty,
    hasPab,
    propSessionId,
    initialSelectedAdvisorId,
    initialProgramExternalId,
  ]);

  // Auto-select the specified program (or first when only one exists)
  useEffect(() => {
    if (selectedTab !== "program" || selectedProgramId !== null || userPrograms.length === 0)
      return;
    const target = initialProgramExternalId
      ? userPrograms.find((p) => p.externalId === initialProgramExternalId)
      : userPrograms[0];
    if (!target) return;
    setSelectedProgramId(target.id);
    setSelectedProgramExternalId(target.externalId);
  }, [selectedTab, selectedProgramId, userPrograms, initialProgramExternalId]);

  // ── PAB queries & logic ───────────────────────────────────────

  const fallbackSessionId = propSessionId ?? session?.id ?? 0;
  const isLoadingSession = propSessionId ? false : sessionLoading;
  const pabSessionId = isAdvisor ? (selectedAchieverSessionId ?? 0) : fallbackSessionId;
  const isOneOnOne = !!selectedPabRecipientUserId;

  const achieverOptions = useMemo(() => {
    if (!advisorPabs?.length) return [];
    return advisorPabs.map((pab) => ({
      sessionId: pab.pabSessionId,
      achieverName: pab.achiever.fullName,
      achieverAvatarUrl: pab.achiever.profilePictureUrl?.trim() || null,
      achieverUserId: pab.achiever.userId ?? null,
    }));
  }, [advisorPabs]);

  // Fallback for when the open session isn't in the dashboard-summary `advisorPabs` list
  // (that list is scoped to selected+accepted+active board entries — AdvisorPabDetailPage
  // and other detail views can open sessions outside that scope).
  const { data: selectedPabDetail } = useQuery({
    queryKey: ["pab-session-detailed", selectedAchieverSessionId],
    queryFn: () => api.pabSessions.getDetailed(selectedAchieverSessionId!),
    enabled: isAdvisor && selectedTab === "pab" && !!selectedAchieverSessionId && isOpen,
    staleTime: 10 * 60 * 1000,
  });

  const selectedAchiever = useMemo(() => {
    if (!isAdvisor) return null;
    const fromList = achieverOptions.find((o) => o.sessionId === selectedAchieverSessionId);
    if (fromList) return fromList;
    if (selectedPabDetail && selectedPabDetail.session.id === selectedAchieverSessionId) {
      return {
        sessionId: selectedPabDetail.session.id,
        achieverName: selectedPabDetail.achiever.name,
        achieverAvatarUrl: selectedPabDetail.achiever.profileUrl,
        achieverUserId: selectedPabDetail.achiever.userId ?? null,
      };
    }
    return null;
  }, [isAdvisor, achieverOptions, selectedAchieverSessionId, selectedPabDetail]);

  // Advisor: keep selected achiever in sync with open state / prop changes
  useEffect(() => {
    if (!isAdvisor || selectedTab !== "pab") return;

    if (!isOpen) {
      wasDrawerOpenRef.current = false;
      previousPropSessionIdRef.current = propSessionId;
      return;
    }

    const propSessionChanged = propSessionId !== previousPropSessionIdRef.current;
    previousPropSessionIdRef.current = propSessionId;

    if (!wasDrawerOpenRef.current) {
      wasDrawerOpenRef.current = true;
      setSelectedAchieverSessionId(propSessionId ?? achieverOptions[0]?.sessionId ?? null);
      return;
    }

    if (propSessionChanged && propSessionId) {
      setSelectedAchieverSessionId(propSessionId);
      return;
    }

    setSelectedAchieverSessionId((current) => {
      if (current === null) return null;
      if (current && achieverOptions.some((o) => o.sessionId === current)) return current;
      return achieverOptions[0]?.sessionId ?? null;
    });
  }, [isOpen, isAdvisor, selectedTab, propSessionId, achieverOptions]);

  // Advisor: which thread a PAB opens on. By default the "To:" is that PAB's
  // achiever, so the advisor lands directly on a DM with them; an opener that
  // asked for the group thread (the inbox's group-chat rows) gets "Everyone".
  //
  // Decided ONCE per open (and again only if the PAB changes). This used to
  // re-run whenever the selected PAB changed, which only happened on the FIRST
  // open — close reset the recipient to "Everyone" but kept the PAB — so the
  // first open landed on the (often empty) DM and every later open on the group
  // thread. Opening an unread group chat from the inbox showed nothing until
  // the drawer was closed and reopened. It also overrode a recipient the user
  // had picked whenever the PAB list refetched.
  const recipientDecidedForRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isAdvisor) return;
    if (!isOpen) {
      recipientDecidedForRef.current = null;
      return;
    }
    if (!selectedAchieverSessionId || recipientDecidedForRef.current === selectedAchieverSessionId)
      return;
    if (openGroupThread) {
      recipientDecidedForRef.current = selectedAchieverSessionId;
      setSelectedPabRecipientUserId(null);
      return;
    }
    // Wait for the achiever to resolve (PAB list or detail) before deciding.
    if (!selectedAchiever?.achieverUserId) return;
    recipientDecidedForRef.current = selectedAchieverSessionId;
    setSelectedPabRecipientUserId(selectedAchiever.achieverUserId);
  }, [isAdvisor, isOpen, openGroupThread, selectedAchieverSessionId, selectedAchiever]);

  const { data: pabMessagesData, isLoading: pabMessagesLoading } = useQuery({
    queryKey: isOneOnOne
      ? ["direct-pab-messages", pabSessionId, selectedPabRecipientUserId]
      : ["pabMessages", pabSessionId],
    queryFn: () =>
      isOneOnOne
        ? api.pabSessions.getDirectMessagesByUserId(pabSessionId, selectedPabRecipientUserId!)
        : api.pabSessions.getMessages(pabSessionId),
    enabled:
      selectedTab === "pab" && !!pabSessionId && isOpen && (!isOneOnOne || !!selectedPabRecipientUserId),
  });

  const { data: selectedAdvisors } = useQuery({
    queryKey: ["selectedAdvisors", pabSessionId],
    queryFn: () => api.pabSessions.getSelectedAdvisors(pabSessionId),
    enabled: selectedTab === "pab" && !!pabSessionId && isOpen,
  });

  // Achiever: `initialSelectedAdvisorId` (a PAB advisorId, from callers like "Message
  // [Advisor]" buttons) needs resolving to that advisor's userId once selectedAdvisors loads.
  useEffect(() => {
    if (!isOpen || isAdvisor) return;
    if (initialSelectedAdvisorId == null) {
      setSelectedPabRecipientUserId(null);
      return;
    }
    if (!selectedAdvisors) return;
    const match = selectedAdvisors.find((a) => a.userId === initialSelectedAdvisorId);
    setSelectedPabRecipientUserId(match?.userId ?? null);
  }, [isOpen, isAdvisor, pabSessionId, initialSelectedAdvisorId, selectedAdvisors]);

  useEffect(() => {
    if (isAdvisor || !selectedAdvisors) return;
    setSelectedPabRecipientUserId((current) => {
      if (current === null) return null;
      return selectedAdvisors.some(
        (a) => a.userId === current && isAdvisorAccepted(a.approvalStatus),
      )
        ? current
        : null;
    });
  }, [isAdvisor, selectedAdvisors]);

  const pabMessages = useMemo(() => {
    const all = pabMessagesData ?? [];
    // Direct messages come from their own per-conversation endpoint — already scoped, no filtering needed.
    if (isOneOnOne) return all;
    return all.filter((m) => !m.messageType || m.messageType === "group");
  }, [pabMessagesData, isOneOnOne]);

  const advisorOptions = useMemo(() => {
    if (isAdvisor || !selectedAdvisors?.length) return [];
    return selectedAdvisors
      .filter((a) => isAdvisorAccepted(a.approvalStatus))
      .map((a) => ({
        advisorId: a.userId,
        advisorName: a.name,
        advisorAvatarUrl: a.profileUrl?.trim() || null,
        userId: a.userId,
      }));
  }, [isAdvisor, selectedAdvisors]);

  const joinedAdvisorNames = useMemo(
    () => (isAdvisor ? [] : advisorOptions.map((a) => a.advisorName)),
    [isAdvisor, advisorOptions],
  );

  // Recipient options for the "To:" dropdown — who this user can DM in the PAB tab.
  const pabRecipientOptions = useMemo<RecipientOption[]>(() => {
    if (isAdvisor) {
      if (!selectedAchiever?.achieverUserId) return [];
      return [
        {
          userId: selectedAchiever.achieverUserId,
          name: selectedAchiever.achieverName,
          avatarUrl: selectedAchiever.achieverAvatarUrl,
        },
      ];
    }
    return advisorOptions.map((a) => ({
      userId: a.userId,
      name: a.advisorName,
      avatarUrl: a.advisorAvatarUrl,
    }));
  }, [isAdvisor, selectedAchiever, advisorOptions]);

  const pabContactName = useMemo(() => {
    if (isAdvisor) return selectedAchiever?.achieverName ?? "Select an achiever";
    if (selectedPabRecipientUserId) {
      const match = advisorOptions.find((a) => a.userId === selectedPabRecipientUserId);
      if (match) return match.advisorName;
    }
    return advisorOptions.length ? "All Experts" : "Expert";
  }, [isAdvisor, selectedAchiever, advisorOptions, selectedPabRecipientUserId]);

  const sendPabMessageMutation = useMutation({
    mutationFn: ({ body, attachmentIds }: SendPayload) =>
      isOneOnOne
        ? // A 1:1 carries attachments exactly like the group thread does; the
          // server is what keeps them out of the shared Resources tab, so
          // withholding the ids here only lost the file.
          api.pabSessions.sendDirectMessageByUserId(
            pabSessionId,
            selectedPabRecipientUserId!,
            body,
            attachmentIds,
          )
        : api.pabSessions.sendMessage(pabSessionId, body, undefined, attachmentIds),
    onMutate: async ({ body }: SendPayload) => {
      const key = isOneOnOne
        ? ["direct-pab-messages", pabSessionId, selectedPabRecipientUserId]
        : ["pabMessages", pabSessionId];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<PabMessage[]>(key);
      const optimistic: PabMessage = {
        id: Date.now(),
        senderId: user?.id ?? "",
        senderName: user?.fullName ?? "You",
        conversationId: pabSessionId,
        body,
        status: "sending",
        createdAt: new Date().toISOString(),
        isRead: false,
      };
      queryClient.setQueryData(key, (old: PabMessage[] = []) => [...old, optimistic]);
      return { prev, key };
    },
    onError: (_err, _body, ctx) => {
      if (ctx?.prev != null) queryClient.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: () => {
      const key = isOneOnOne
        ? ["direct-pab-messages", pabSessionId, selectedPabRecipientUserId]
        : ["pabMessages", pabSessionId];
      queryClient.invalidateQueries({ queryKey: key });
    },
  });

  // ── Program queries & logic ───────────────────────────────────

  const isProgramDirect = selectedTab === "program" && !!selectedProgramRecipientUserId;

  const { data: programMembers } = useQuery({
    queryKey: ["programMembers", selectedProgramExternalId],
    queryFn: () => programsApi.getMembers(selectedProgramExternalId!),
    enabled: selectedTab === "program" && !!selectedProgramExternalId && isOpen,
  });

  // Recipient options for the "To:" dropdown — who this user can DM in the Program tab.
  const programRecipientOptions = useMemo<RecipientOption[]>(() => {
    if (!programMembers) return [];
    if (isAdvisor) {
      return programMembers.achievers
        .filter((m) => m.status === "ACTIVE" && !!m.user_id)
        .map((m) => ({ userId: m.user_id, name: m.full_name ?? "Achiever", avatarUrl: m.profile_picture_url }));
    }
    return [...(programMembers.lead_advisors ?? []), ...(programMembers.co_advisors ?? [])]
      .filter((m) => m.advisor_profile !== null && m.status === "ACTIVE" && !!m.user_id)
      .map((m) => ({ userId: m.user_id, name: m.full_name ?? "Expert", avatarUrl: m.profile_picture_url }));
  }, [programMembers, isAdvisor]);

  const { data: programMessagesData, isLoading: programMessagesLoading } = useQuery({
    queryKey: isProgramDirect
      ? ["direct-program-messages", selectedProgramId, selectedProgramRecipientUserId]
      : ["programMessages", selectedProgramId],
    queryFn: () =>
      isProgramDirect
        ? programsApi.getDirectProgramMessagesByUser(selectedProgramId!, selectedProgramRecipientUserId!)
        : programsApi.getProgramMessages(selectedProgramId!),
    enabled:
      selectedTab === "program" &&
      !!selectedProgramId &&
      isOpen &&
      (!isProgramDirect || !!selectedProgramRecipientUserId),
  });

  const programMessages = useMemo(() => {
    const all = programMessagesData ?? [];
    // Direct messages come from their own per-conversation endpoint — already scoped, no filtering needed.
    if (isProgramDirect) return all;
    return all.filter((m) => !m.messageType || m.messageType === "group");
  }, [programMessagesData, isProgramDirect]);

  const selectedProgram = useMemo(
    () => userPrograms.find((p) => p.id === selectedProgramId) ?? null,
    [userPrograms, selectedProgramId],
  );

  const programContactName = useMemo(() => {
    if (!selectedProgram) return "Programs";
    if (!selectedProgramRecipientUserId) return selectedProgram.name;
    return (
      programRecipientOptions.find((m) => m.userId === selectedProgramRecipientUserId)?.name ??
      selectedProgram.name
    );
  }, [selectedProgram, selectedProgramRecipientUserId, programRecipientOptions]);

  const sendProgramMessageMutation = useMutation({
    mutationFn: ({ body, attachmentIds }: SendPayload) =>
      isProgramDirect
        ? // See sendPabMessageMutation: the 1:1 endpoint takes attachments too,
          // and the Resources tab exclusion is enforced server-side.
          programsApi.sendDirectProgramMessage(
            selectedProgramId!,
            selectedProgramRecipientUserId!,
            body,
            attachmentIds,
          )
        : programsApi.sendProgramMessage(selectedProgramId!, body, undefined, attachmentIds),
    onMutate: async ({ body }: SendPayload) => {
      const key = isProgramDirect
        ? ["direct-program-messages", selectedProgramId, selectedProgramRecipientUserId]
        : ["programMessages", selectedProgramId];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<PabMessage[]>(key);
      const optimistic: PabMessage = {
        id: Date.now(),
        senderId: user?.id ?? "",
        senderName: user?.fullName ?? "You",
        conversationId: selectedProgramId ?? 0,
        body,
        status: "sending",
        createdAt: new Date().toISOString(),
        isRead: false,
      };
      queryClient.setQueryData(key, (old: PabMessage[] = []) => [...old, optimistic]);
      return { prev, key };
    },
    onSuccess: (_data, { body }: SendPayload) => {
      if (isProgramDirect && selectedProgramId) {
        tracking.programDirectMessageSent({
          program_id: String(selectedProgramId),
          recipient_user_id: selectedProgramRecipientUserId,
          message_length: body.length,
        });
      }
    },
    onError: (_err, _body, ctx) => {
      if (ctx?.prev != null) queryClient.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: () => {
      const key = isProgramDirect
        ? ["direct-program-messages", selectedProgramId, selectedProgramRecipientUserId]
        : ["programMessages", selectedProgramId];
      queryClient.invalidateQueries({ queryKey: key });
    },
  });

  // ── Send handler ──────────────────────────────────────────────

  /** Which programme the CURRENT conversation belongs to.
   *
   *  This is the conversation -> programme mapping the Resources tab keys off:
   *  a PAB session id and an Expert Program id are both programs.id, so an
   *  attachment uploaded here lands in that programme's Resources tab
   *  automatically. */
  /** Which programme the CURRENT conversation belongs to — the mapping the
   *  Resources tab keys off. A PAB session id and an Expert Program id are both
   *  programs.id, so a group-chat attachment lands in that programme's
   *  Resources tab; a 1:1 attachment is kept out of it server-side. */
  const attachmentProgramId = selectedTab === "pab" ? pabSessionId : selectedProgramId;
  const attachments = useMessageAttachments(attachmentProgramId);


  const handleSend = useCallback(() => {
    // A staged attachment is enough on its own — this used to require text,
    // which made an attachment-only message impossible to send.
    if ((!messageInput.trim() && attachments.pending.length === 0) || sending) return;
    const trimmed = messageInput.trim();
    const attachmentIds = attachments.pending.map((a) => a.id);
    // A staged file becomes a programme resource only when the message linking
    // it is actually sent, and only if that message went to the whole group —
    // so the Resources tab is refreshed here rather than at upload time, and
    // never for a 1:1.
    const isDirectSend = selectedTab === "pab" ? isOneOnOne : isProgramDirect;
    const refreshResources = () => {
      if (attachmentIds.length > 0 && !isDirectSend) attachments.refreshProgramResources();
    };

    if (selectedTab === "pab") {
      tracking.pabMessageSent({
        pab_session_id: pabSessionId,
        channel: isOneOnOne ? "direct" : "group",
        message_length: trimmed.length,
        user_role: getPrimaryRole(user),
      });
      setSending(true);
      sendPabMessageMutation.mutate(
        { body: trimmed, attachmentIds },
        { onSuccess: refreshResources, onSettled: () => setSending(false) },
      );
    } else if (selectedTab === "program") {
      setSending(true);
      sendProgramMessageMutation.mutate(
        { body: trimmed, attachmentIds },
        { onSuccess: refreshResources, onSettled: () => setSending(false) },
      );
    }

    attachments.clear();
    setMessageInput("");
  }, [
    messageInput,
    sending,
    selectedTab,
    sendPabMessageMutation,
    sendProgramMessageMutation,
    pabSessionId,
    isOneOnOne,
    isProgramDirect,
    user,
    // REQUIRED, not tidy-up: without it handleSend keeps the closure from the
    // render before the file was attached, so attaching and immediately
    // pressing Send would post the previous (usually empty) id list and
    // silently drop the attachment.
    attachments,
  ]);


  // ── Derived rendering values ──────────────────────────────────

  const isPabNoSession = !pabSessionId && !isLoadingSession;
  const isPabLoading = isLoadingSession || pabMessagesLoading;

  const isProgramNoSession = !selectedProgramId;
  const canSend =
    selectedTab === "pab"
      ? !!pabSessionId
      : selectedTab === "program"
        ? !!selectedProgramId
        : false;

  const contactName =
    selectedTab === "pab" ? pabContactName : selectedTab === "program" ? programContactName : "";

  // "To:" dropdown — recipient list + current selection for whichever tab is active
  const activeRecipientOptions =
    selectedTab === "pab" ? pabRecipientOptions : selectedTab === "program" ? programRecipientOptions : [];
  const activeRecipientUserId =
    selectedTab === "pab" ? selectedPabRecipientUserId : selectedProgramRecipientUserId;
  const handleRecipientChange = (value: string) => {
    const nextUserId = value === EVERYONE_VALUE ? null : value;
    if (selectedTab === "pab") setSelectedPabRecipientUserId(nextUserId);
    else if (selectedTab === "program") setSelectedProgramRecipientUserId(nextUserId);
  };

  return (
    <div
      className={`fixed inset-0 z-50 overflow-hidden transition-all duration-300 ${isOpen ? "pointer-events-auto" : "pointer-events-none"}`}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${isOpen ? "bg-opacity-50" : "bg-opacity-0"}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`absolute right-0 top-0 h-full w-full bg-theme-bg-surface shadow-xl flex flex-col overflow-hidden transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ maxWidth: "499px" }}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-theme-border">
            <div className="flex items-start gap-2">
              {/* Back button — pinned to left. Plain <button> rather than the
                  shared Button component: Button's `size="sm"` bakes in
                  px-6/py-3 (and `.btn` itself adds px-4/py-2 under that),
                  which would fight any padding passed via className here.
                  Sized with a fixed w-9/h-9 box instead of padding, so there's
                  nothing to conflict with; -ml-2 offsets that box's built-in
                  8px of left space so the arrow's edge lines up with the
                  message bubbles below (both anchored to the header/
                  message-list's shared `px-6`). */}
              <button
                type="button"
                onClick={onBack ?? onClose}
                aria-label="Back"
                className="flex-shrink-0 mt-0.5 -ml-2 flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-theme-bg-hover focus:outline-none focus:ring-2 focus:ring-primary-350 cursor-pointer"
              >
                <ArrowLeft size={20} className="text-theme-text-muted" />
              </button>

              {/* Title + subtitle — grows to fill space, wraps on overflow */}
              <div className="flex-1 min-w-0 py-1">
                <Heading
                  as="h2"
                  className="font-inter font-bold text-20 leading-7 text-theme-text-heading break-words"
                >
                  {chatTitle ?? "Messages"}
                </Heading>
                {(chatSubtitle ?? (!chatTitle ? contactName : null)) && (
                  <Text
                    as="p"
                    className="font-inter text-14 leading-5 text-theme-text-secondary mt-0.5 break-words"
                    title={chatSubtitle ?? contactName}
                  >
                    {chatSubtitle ?? contactName}
                  </Text>
                )}
              </div>

              {/* Close button — pinned to right */}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="flex-shrink-0 mt-0.5 p-2 hover:bg-theme-bg-hover rounded-full transition-colors cursor-pointer"
                aria-label="Close"
              >
                <X size={20} className="text-theme-text-muted" />
              </Button>
            </div>
          </div>

          


          {/* Conversation area */}
          <div className="flex-1 min-h-0 overflow-y-auto py-4 px-6">
            {!selectedTab ? (
              <ConversationState>
                <MessageSquare size={40} className="mb-3 text-theme-text-muted" />
                <Text as="p" className="font-inter text-14 text-theme-text-muted">
                  Select a conversation to get started.
                </Text>
              </ConversationState>
            ) : selectedTab === "pab" ? (
              /* ── PAB messages ── */
              isPabNoSession ? (
                <ConversationState>
                  <MessageSquare size={40} className="mb-3 text-theme-text-muted" />
                  <Text as="p" className="font-inter text-14 text-theme-text-muted">
                    {isAdvisor
                      ? "Select an achiever from members to view messages."
                      : "No active session. Start a session to view messages."}
                  </Text>
                </ConversationState>
              ) : isPabLoading ? (
                <MessageSkeleton />
              ) : pabMessages.length === 0 ? (
                <ConversationState>
                  {!isAdvisor && (
                    <JoinedConversationNotices advisorNames={joinedAdvisorNames} className="mb-4" />
                  )}
                  <MessageSquare size={40} className="mb-3 text-theme-text-muted" />
                  <Text as="p" className="font-inter text-14 text-theme-text-muted">
                    {isOneOnOne
                      ? isAdvisor
                        ? "No messages with this achiever yet"
                        : "No messages with this expert yet"
                      : isAdvisor
                        ? "No messages yet. Send a message to this achiever."
                        : "No messages yet. Send a message below."}
                  </Text>
                </ConversationState>
              ) : (
                <div className="space-y-4">
                  {!isAdvisor && <JoinedConversationNotices advisorNames={joinedAdvisorNames} />}
                  {pabMessages.map((msg) => (
                    <MessageBubble
                      key={msg.id}
                      message={msg}
                      currentUserId={user?.id}
                      currentUserRole={getPrimaryRole(user)}
                      programId={pabSessionId}
                    />
                  ))}
                </div>
              )
            ) : /* ── Program messages ── */
            isProgramNoSession ? (
              <ConversationState>
                <Users size={40} className="mb-3 text-theme-text-muted" />
                <Text as="p" className="font-inter text-14 text-theme-text-muted">
                  {userPrograms.length === 0
                    ? "No expert programs found."
                    : "Select a program to view messages."}
                </Text>
              </ConversationState>
            ) : programMessagesLoading ? (
              <MessageSkeleton />
            ) : programMessages.length === 0 ? (
              <ConversationState>
                <MessageSquare size={40} className="mb-3 text-theme-text-muted" />
                <Text as="p" className="font-inter text-14 text-theme-text-muted">
                  {isProgramDirect
                    ? "No messages with this expert yet."
                    : "No messages yet. Send a message to the group."}
                </Text>
              </ConversationState>
            ) : (
              <div className="space-y-4">
                {programMessages.map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    currentUserId={user?.id}
                    currentUserRole={getPrimaryRole(user)}
                    programId={selectedProgramId}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Dashed separator above footer */}
          <div className="border-t border-dashed border-theme-border" />

          <MessageComposer
            value={messageInput}
            onChange={setMessageInput}
            onSend={handleSend}
            sending={sending}
            disabled={!canSend}
            placeholder={
              (selectedTab === "program" && isProgramDirect) ||
              (selectedTab === "pab" && isOneOnOne)
                ? "Type a direct message..."
                : "Type a message..."
            }
            attachments={attachments.pending}
            onAttachFiles={(files) => void attachments.attach(files)}
            onRemoveAttachment={attachments.remove}
            isUploading={attachments.isUploading}
            canAttach={attachments.canAttach}
            /* Only this drawer has a recipient picker, so it comes in as a
               slot rather than living in the shared composer. */
            header={
              (selectedTab === "pab" || selectedTab === "program") &&
              activeRecipientOptions.length > 0 ? (
                <div className="flex items-center gap-2 mb-3">
                  <Text as="span" className="font-inter text-13 text-theme-text-muted flex-shrink-0">
                    To:
                  </Text>
                  <SelectRoot
                    name="message-recipient"
                    value={activeRecipientUserId ?? EVERYONE_VALUE}
                    onValueChange={handleRecipientChange}
                    options={[
                      { label: "Everyone", value: EVERYONE_VALUE },
                      ...activeRecipientOptions.map((r) => ({ label: r.name, value: r.userId })),
                    ]}
                    className="!w-auto !h-8 !min-h-0 rounded-full !px-3 !py-1 text-13 bg-theme-msg-bubble-self-bg border-transparent text-theme-msg-bubble-self-name font-medium"
                  />
                </div>
              ) : null
            }
          />
        </div>
      </div>
    </div>
  );
};
